/**
 * The Q₁₀ temperature coefficient: how many times faster a process runs for a 10 °C
 * (or 10 K) rise in temperature.
 *
 * Q₁₀ = (R₂ ÷ R₁)^(10 ÷ (T₂ − T₁))  and so  R₂ = R₁ × Q₁₀^((T₂ − T₁) ÷ 10).
 *
 * Temperatures may be in °C, K (the same interval) or °F, whose degree is 5/9 of a
 * Celsius degree: a 10 °C interval is 18 °F. Only temperature *differences* enter the
 * formula, so the offset between scales does not matter — except for the Arrhenius
 * activation energy, which needs absolute temperature.
 */

export type TempUnit = "c" | "k" | "f";

/** The gas constant, J/(mol·K) (CODATA 2018). */
export const GAS_CONSTANT = 8.314_462_618;

/** The size of one degree of `unit`, in Celsius degrees. */
function degreeSize(unit: TempUnit): number {
  return unit === "f" ? 5 / 9 : 1;
}

/** A temperature difference in `unit` as Celsius degrees. */
export function intervalInCelsius(delta: number, unit: TempUnit): number {
  return delta * degreeSize(unit);
}

export function toKelvin(temperature: number, unit: TempUnit): number {
  if (unit === "k") {
    return temperature;
  }
  return unit === "c"
    ? temperature + 273.15
    : ((temperature - 32) * 5) / 9 + 273.15;
}

const positive = (x: number) => Number.isFinite(x) && x > 0;

/** Q₁₀ from two rates at two temperatures; null when the inputs cannot give one. */
export function q10FromRates(
  rate1: number,
  temp1: number,
  rate2: number,
  temp2: number,
  unit: TempUnit
): number | null {
  const span = intervalInCelsius(temp2 - temp1, unit);
  if (!(positive(rate1) && positive(rate2)) || span === 0) {
    return null;
  }
  return (rate2 / rate1) ** (10 / span);
}

/** The rate at `temp2` given the rate at `temp1` and Q₁₀. */
export function rateAtTemperature(
  rate1: number,
  temp1: number,
  temp2: number,
  q10: number,
  unit: TempUnit
): number | null {
  if (!(positive(rate1) && positive(q10))) {
    return null;
  }
  return rate1 * q10 ** (intervalInCelsius(temp2 - temp1, unit) / 10);
}

/**
 * The temperature at which the rate reaches `rate2`, from the rate at `temp1` and Q₁₀:
 * T₂ = T₁ + 10 ln(R₂ ÷ R₁) ÷ ln Q₁₀, converted back to `unit`. Null when Q₁₀ = 1 (the
 * rate never changes).
 */
export function temperatureForRate(
  rate1: number,
  temp1: number,
  rate2: number,
  q10: number,
  unit: TempUnit
): number | null {
  if (!(positive(rate1) && positive(rate2) && positive(q10)) || q10 === 1) {
    return null;
  }
  const celsius = (10 * Math.log(rate2 / rate1)) / Math.log(q10);
  return temp1 + celsius / degreeSize(unit);
}

/** The rise in temperature, in `unit`, that doubles the rate; null when Q₁₀ ≤ 1. */
export function doublingInterval(q10: number, unit: TempUnit): number | null {
  if (!(q10 > 1)) {
    return null;
  }
  return (10 * Math.LN2) / Math.log(q10) / degreeSize(unit);
}

/** The percentage change in rate for one degree of `unit`. */
export function percentPerDegree(q10: number, unit: TempUnit): number {
  return (q10 ** (degreeSize(unit) / 10) - 1) * 100;
}

/**
 * The Arrhenius activation energy in J/mol that gives the same two rates:
 * Eₐ = R ln(R₂ ÷ R₁) ÷ (1/T₁ − 1/T₂), with T in kelvin. Null for non-positive
 * absolute temperatures or equal temperatures.
 */
export function activationEnergy(
  rate1: number,
  temp1: number,
  rate2: number,
  temp2: number,
  unit: TempUnit
): number | null {
  const k1 = toKelvin(temp1, unit);
  const k2 = toKelvin(temp2, unit);
  if (!(positive(rate1) && positive(rate2) && k1 > 0 && k2 > 0) || k1 === k2) {
    return null;
  }
  return (GAS_CONSTANT * Math.log(rate2 / rate1)) / (1 / k1 - 1 / k2);
}

export interface RatePoint {
  temperature: number;
  rate: number;
  /** The rate relative to the reference rate. */
  factor: number;
}

/** The rate at `count` temperatures `step` apart, starting `before` steps below `temp1`. */
export function rateTable(
  rate1: number,
  temp1: number,
  q10: number,
  unit: TempUnit,
  step = 5,
  before = 2,
  count = 9
): RatePoint[] {
  return Array.from({ length: count }, (_, i) => {
    const temperature = temp1 + (i - before) * step;
    const factor = q10 ** (intervalInCelsius(temperature - temp1, unit) / 10);
    return { temperature, rate: rate1 * factor, factor };
  });
}
